import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { List, X, Globe } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../i18n/translations';
import { Button } from './ui/button';

export const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { key: 'home', to: '/' },
    { key: 'about', to: '/about' },
    { key: 'services', to: '/services' },
    { key: 'projects', to: '/projects' },
    { key: 'gallery', to: '/gallery' },
    { key: 'news', to: '/news' },
    { key: 'blog', to: '/blog' },
    { key: 'partners', to: '/partners' },
    { key: 'contact', to: '/contact' },
  ];

  const isActive = (to) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5"
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img
              src={companyInfo.logo}
              alt="JKM Engineering"
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                className={`text-sm font-medium transition-colors tracking-wide uppercase ${
                  isActive(item.to)
                    ? 'text-[#FF6B00]'
                    : 'text-[#0B1320] hover:text-[#FF6B00]'
                }`}
                data-testid={`nav-${item.key}`}
              >
                {t.nav[item.key]}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#0B1320] hover:text-[#FF6B00] transition-colors"
              data-testid="language-toggle"
            >
              <Globe size={20} weight="bold" />
              <span className="uppercase tracking-wider">{language === 'fr' ? 'EN' : 'FR'}</span>
            </button>

            {/* CTA Button */}
            <Button
              asChild
              className="hidden md:flex bg-[#FF6B00] hover:bg-[#FF6B00]/90 text-white rounded-none px-6 py-2 font-medium tracking-wide"
              data-testid="header-contact-btn"
            >
              <Link to="/contact">
                {t.nav.contact}
              </Link>
            </Button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-[#0B1320]"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={28} weight="bold" /> : <List size={28} weight="bold" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-black/5"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col py-4">
              {navItems.map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-6 py-4 text-base font-medium transition-colors tracking-wide uppercase ${
                    isActive(item.to)
                      ? 'text-[#FF6B00] bg-[#FFF7ED]'
                      : 'text-[#0B1320] hover:bg-[#F3F4F6] hover:text-[#FF6B00]'
                  }`}
                  data-testid={`mobile-nav-${item.key}`}
                >
                  {t.nav[item.key]}
                </Link>
              ))}
              <div className="px-6 py-4">
                <Button
                  asChild
                  className="w-full bg-[#FF6B00] hover:bg-[#FF6B00]/90 text-white rounded-none py-3 font-medium tracking-wide"
                >
                  <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                    {t.nav.contact}
                  </Link>
                </Button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
